import { Text } from '@doordash/design-language'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import { Interpolate } from '@doordash/lib-i18n'
import * as React from 'react'
import { connect } from 'react-redux'
import { toggleAlreadyStartedSigningUpModal } from '../../../actions'
import { COUNTRY_SHORTCODES } from '../../../constants'
import { GERMANY_DX_ONBOARDING_V2 } from '../../../experimentConstants'
import i18n from '../../../i18n'
import {
  AlreadyStartedEnum,
  AlreadyStartedSigningUpModalProps,
  SupportedCountryCode,
} from '../../../models/types'
import DetailSignupPage from '../detail-signup'
import SimpleSignUpPage from '../simple-sign-up'
import { Mode } from '../simple-sign-up/SimpleSignUpPage'
import { countryCodeToPrivacyPolicyUrl, countryCodeToTosUrl } from './legalUrls'
import t from './strings'

/**
 * Some countries should show a simple sign up page that
 * doesn't contain particular business messaging (eg. Dashers being
 * able to choose exactly when they can dash)
 */
const simpleSignUpPageCountryCodes: SupportedCountryCode[] = [
  COUNTRY_SHORTCODES.DE,
]

/**
 * Some countries have disclaimers that need to be rendered
 */
const simpleSignUpPageCountryCodeToDisclaimer: Partial<Record<
  SupportedCountryCode,
  JSX.Element
>> = {
  DE: (
    <Text styles={Text.Styles.Body2}>
      <Interpolate
        i18n={i18n}
        i18nKey={t.employmentDisclaimerDE()}
        components={[
          // Disable due to false positives
          /* eslint-disable-next-line jsx-a11y/anchor-has-content */
          <a
            href={
              'https://de.jobandtalent.com/lieferfahrer-doordash/?medium=Doordash/'
            }
            target="_blank"
          >
            Apply
          </a>,
        ]}
      />
    </Text>
  ),
}

type SignUpProps = {
  signupCountryCode?: SupportedCountryCode
  toggleAlreadyStartedSigningUpModal: (
    data: AlreadyStartedSigningUpModalProps
  ) => Promise<void>
  alreadyStartedSigningUpModal: AlreadyStartedSigningUpModalProps
}

/**
 * SignUpPage
 *
 * Controls which signup page should be rendered.
 * For some geos, the detailed sign up page is unsuitable because
 * the additional information does not apply.
 */
const SignUpPage: React.FC<SignUpProps> = ({
  signupCountryCode,
  toggleAlreadyStartedSigningUpModal,
  alreadyStartedSigningUpModal,
}) => {
  const handleExistingUserRequest = (
    options?: AlreadyStartedSigningUpModalProps
  ) => {
    toggleAlreadyStartedSigningUpModal({
      type: AlreadyStartedEnum.Default,
      ...options,
      isShowing: true,
    })
  }

  const handleModalClose = () => {
    toggleAlreadyStartedSigningUpModal({
      isShowing: false,
    })
  }

  const {
    isShowing: isAlreadyStartedSigningUpModalOpen,
  } = alreadyStartedSigningUpModal

  if (simpleSignUpPageCountryCodes.includes(signupCountryCode)) {
    const mode: Mode = isFeatureFlagEnabled(GERMANY_DX_ONBOARDING_V2)
      ? 'v2'
      : 'default'

    return (
      <SimpleSignUpPage
        disclaimer={simpleSignUpPageCountryCodeToDisclaimer[signupCountryCode]}
        termsOfAgreementUrl={countryCodeToTosUrl[signupCountryCode]}
        privacyPolicyUrl={countryCodeToPrivacyPolicyUrl[signupCountryCode]}
        preselectedCountryCode={signupCountryCode}
        applicantCountryCode={signupCountryCode}
        onExistingUserRequest={handleExistingUserRequest}
        onExistingUserModalCloseRequest={handleModalClose}
        isExistingUserModalOpen={isAlreadyStartedSigningUpModalOpen}
        mode={mode}
      />
    )
  }
  /**
   * IMPROVE @peterj35-dd: Tease apart the submitDasherApplication handler
   * from the DetailSignupPage to indicate at a higher level
   * that ultimately the Simple and Detail SignupPages leverage
   * the same APIs
   */
  return <DetailSignupPage />
}

const mapStateToProps = state => ({
  alreadyStartedSigningUpModal: state.alreadyStartedSigningUpModal,
  signupCountryCode: state.signupCountryShortname,
})

const mapDispatchToProps = {
  toggleAlreadyStartedSigningUpModal,
}

export default connect(mapStateToProps, mapDispatchToProps)(SignUpPage)
